import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './accordion.module.scss';

type Key = string | number;

export type AccordionItem = {
    key: Key;
    header: ReactNode;
    content: ReactNode;
};

type AccordionProps = {
    items: AccordionItem[];
    defaultActiveKey?: Key | null;
    className?: string;
};

type AccordionRowProps = {
    item: AccordionItem;
    derivedKey: Key;
    isActive: boolean;
    toggleItem: (key: Key) => void;
};

const AccordionRow: FC<AccordionRowProps> = ({
    item,
    derivedKey,
    isActive,
    toggleItem,
}) => {
    const panelId = `accordion-panel-${derivedKey}`;
    const headerId = `accordion-header-${derivedKey}`;
    const contentRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<string>('0px');

    const updateHeight = () => {
        const el = contentRef.current;
        if (!el) return;

        const nextHeight = isActive ? `${el.scrollHeight}px` : '0px';
        setMaxHeight(nextHeight);
    };

    useEffect(() => {
        updateHeight();
    }, [isActive, item.content]);

    useEffect(() => {
        const handler = () => updateHeight();
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, [isActive]);

    return (
        <div
            className={cn(styles.item, {
                [styles.active]: isActive,
            })}
        >
            <button
                type="button"
                className={styles.header}
                onClick={() => toggleItem(derivedKey)}
                aria-expanded={isActive}
                aria-controls={panelId}
                id={headerId}
            >
                <div className={styles.headerContent}>{item.header}</div>
                <span className={styles.icon} aria-hidden="true">
                    <span className={styles.iconLine} />
                    <span
                        className={cn(styles.iconLineVertical, {
                            [styles.iconLineVerticalHidden]: isActive,
                        })}
                    />
                </span>
            </button>
            <div
                id={panelId}
                role="region"
                aria-labelledby={headerId}
                aria-hidden={!isActive}
                className={styles.body}
                style={{
                    maxHeight,
                    opacity: isActive ? 1 : 0,
                    pointerEvents: isActive ? 'auto' : 'none',
                }}
            >
                <div className={styles.bodyInner} ref={contentRef}>
                    {item.content}
                </div>
            </div>
        </div>
    );
};

export const Accordion: FC<AccordionProps> = ({
    items,
    defaultActiveKey = null,
    className,
}) => {
    const [activeKey, setActiveKey] = useState<Key | null>(defaultActiveKey);

    const toggleItem = (key: Key) => {
        setActiveKey(prev => (prev === key ? null : key));
    };

    const keyedItems = useMemo(
        () =>
            items.map((item, index) => ({
                item,
                derivedKey: `${item.key}-${index}`,
            })),
        [items]
    );

    const renderedItems = useMemo(
        () =>
            keyedItems.map(({ item, derivedKey }) => (
                <AccordionRow
                    key={derivedKey}
                    item={item}
                    derivedKey={derivedKey}
                    isActive={derivedKey === activeKey}
                    toggleItem={toggleItem}
                />
            )),
        [keyedItems, activeKey]
    );

    return (
        <div className={cn(styles.accordion, className)}>{renderedItems}</div>
    );
};

export default Accordion;
